import {
  CanActivate,
  CanActivateChild,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  NavigationExtras,
} from "@angular/router";
import { Observable } from "rxjs";
import { Injectable } from "@angular/core";
import { map } from "rxjs/operators";
import { ErrorType } from "./../../../app/error/models/error-type.enum";
import { AppConfigurationService } from "./services/ipc-app-configuration.service";
import { RouteGuardAppConfigurationOptions } from "./models";

@Injectable()
export class AppConfigurationGuard implements CanActivate, CanActivateChild {
  constructor(private router: Router, private appConfigurationService: AppConfigurationService) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean> | Promise<boolean> {
    const configuration = route.data["appConfiguration"];
    return this.hasAccess(configuration)
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean> | Promise<boolean> {
    if (childRoute.data["skipGuard"] && childRoute.data["skipGuard"] === true) {
      return true;
    }

    const configuration = childRoute.data["appConfiguration"] || this.findAvailableConfiguration(childRoute);

    return this.hasAccess(configuration);
  }

  private hasAccess(configuration: RouteGuardAppConfigurationOptions): Observable<boolean> {
    return this.appConfigurationService.configurationsLoaded.pipe(
      map((isLoaded$) => {
        const errorFlow = () => { this.router.navigate(["invoices-payments", "error"], { fragment: ErrorType.Features } as NavigationExtras); return false; }
        const forbiddenFlow = () => { this.router.navigate(["invoices-payments", "error"], { fragment: ErrorType.Forbidden } as NavigationExtras); return false; }
        const fallbackFlow = () => { this.router.navigate(configuration.fallbackUrl); return false; }

        if (!configuration) {
          console.error("When using the AppConfiguration guard a configuration object is required");
          return errorFlow();
        }

        if (!isLoaded$) {
          console.error("Configurations not loaded");
          return errorFlow();
        }

        const { configurationName, negate } = this.parseConfigurationString(configuration.name);
        const isEnabled = this.appConfigurationService.isEnabled(configurationName);
        const canContinue = negate ? !isEnabled : isEnabled;

        if (!canContinue && configuration.fallbackUrl) {
          return fallbackFlow();
        }

        if (!canContinue) {
          return forbiddenFlow();
        }

        return true;
      })
    );
  }

  private parseConfigurationString(configuration: string): {
    configurationName: string;
    negate: boolean;
  } {
    const negate = configuration.startsWith("!");
    const configurationName = negate ? configuration.substring(1) : configuration;
    return { configurationName, negate };
  }

  private findAvailableConfiguration(node: ActivatedRouteSnapshot) {
    let parent = node.parent;
    while (parent && !parent.data["appConfiguration"]) {
      parent = parent.parent;
    }

    return parent.data["appConfiguration"];
  }
}
